/**
 * A tarja silenciosa de "a outra pessoa abriu uma sessão de cinema".
 *
 * Porte de `lib/features/watch_party/presentation/widgets/incoming_watch_party_banner.dart`
 * e do pedaço do `lib/core/di/app_root.dart` que liga e desliga o ouvinte junto
 * com a sessão. **Sem som e sem vibração: só imagem.** Um toque abre a tela da
 * sessão e entra na chamada.
 *
 * Mora na raiz do app, fora das rotas, então aparece esteja a pessoa em que tela
 * estiver. Some sozinha quando alguém entra na tela da sessão, que chama
 * `pararDeEscutarConvites()` ao nascer.
 */

import { useEffect, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icone } from '../../componentes/Icone';
import { armazem } from '../../nucleo/api';
import {
  COLECAO_SALAS,
  conviteDeAgora,
  escutarConvites,
  inscreverConvite,
  pararDeEscutarConvites,
  sufixoDoEpisodio,
  traduzir,
  usarDupla,
  type FichaDaSala,
} from '@nosso-sempre/shared';
import { CAMINHO_DA_SALA } from './botaoJunto';
import './cinema.css';

export function TarjaDeSessao() {
  const ir = useNavigate();
  const dupla = usarDupla();
  const local = useLocation();
  const naSessao = local.pathname === CAMINHO_DA_SALA;

  const convite = useSyncExternalStore(inscreverConvite, conviteDeAgora);

  /**
   * Liga o ouvinte, como o `_syncListener` do `AppRoot` faz quando a casa é de
   * duas pessoas. Fica desligado dentro da própria sessão: lá a chamada já está
   * acontecendo e a tarja anunciaria por cima dela.
   */
  useEffect(() => {
    if (!dupla.eu || !dupla.parceiro || naSessao) return;
    escutarConvites(dupla.eu, dupla.parceiro);
    return () => pararDeEscutarConvites();
  }, [dupla.eu, dupla.parceiro, naSessao]);

  if (!convite) return null;

  const abrir = () => {
    // A ficha em `voice_rooms` guarda os dados da chamada; a de
    // `watch_party_rooms` guarda os da mídia. É a segunda que se lê aqui, porque
    // a sessão pode ter acabado com a tarja ainda na tela: não entra numa sala
    // morta, nem cria ela de novo.
    const sala = armazem.documento<FichaDaSala>(COLECAO_SALAS, convite.sala);
    // Some com a tarja em qualquer um dos dois caminhos: ou a sessão morreu, ou
    // a tela dela está prestes a nascer e não pode nascer anunciada por cima.
    pararDeEscutarConvites();
    if (sala && sala.active !== true) return;
    ir(CAMINHO_DA_SALA);
  };

  const inicial = convite.nomeDeQuemChama ? convite.nomeDeQuemChama[0]!.toUpperCase() : '?';
  const linha2 = convite.titulo
    ? `Assistindo ${convite.titulo}${sufixoDoEpisodio(convite)}`
    : traduzir('Toque para entrar');

  return (
    <div className="cine-tarja">
      <div className="cine-tarja-pilha">
        <button type="button" className="cine-tarja-carta" onClick={abrir}>
          <span className="cine-tarja-inicial" aria-hidden="true">
            {inicial}
          </span>
          <span className="cine-tarja-texto">
            <span className="cine-tarja-linha1">
              {traduzir('{quem} abriu uma sessão de cinema', { quem: convite.nomeDeQuemChama })}
            </span>
            <span className="cine-tarja-linha2">{linha2}</span>
          </span>
          <span className="cine-tarja-entrar">
            {traduzir('Join')}
            <Icone nome="adiante" tamanho={14} />
          </span>
        </button>
        <button type="button" className="cine-tarja-fechar" onClick={pararDeEscutarConvites}>
          <Icone nome="fechar" tamanho={15} rotulo={traduzir('Fechar o aviso')} />
        </button>
      </div>
    </div>
  );
}
